import React, { useState, useEffect, useRef } from 'react';
import { ModalType } from '../types';

interface NavProps {
  onOpenModal: (type: ModalType) => void;
}

const navItems = [
  { label: 'About', sectionId: 'difference' },
  { label: 'Services', sectionId: 'services' },
  { label: 'Work', sectionId: 'work' },
];

const Nav: React.FC<NavProps> = ({ onOpenModal }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>('');

  // Track which nav sections are currently visible
  const visibleSections = useRef<Set<string>>(new Set());

  /* ── Scroll shadow ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Smart active state ── */
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    navItems.forEach(({ sectionId }) => {
      const el = document.getElementById(sectionId);
      if (!el) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleSections.current.add(sectionId);
          } else {
            visibleSections.current.delete(sectionId);
          }

          // Pick the first navItem that is currently visible
          // (preserves document order regardless of scroll direction)
          const next = navItems.find((n) => visibleSections.current.has(n.sectionId));
          setActiveId(next?.sectionId ?? '');
        },
        { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
      );

      obs.observe(el);
      observers.push(obs);
    });

    return () => {
      observers.forEach((o) => o.disconnect());
      // eslint-disable-next-line react-hooks/exhaustive-deps
      visibleSections.current.clear();
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-10"
      style={{
        height: 68,
        background: scrolled ? 'rgba(8,7,6,0.97)' : 'rgba(13,12,11,0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${scrolled ? 'rgba(184,154,90,0.2)' : 'rgba(184,154,90,0.08)'}`,
        transition: 'background 0.4s ease, border-color 0.4s ease',
      }}
    >
      {/* ── Logo ── */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="flex-shrink-0 flex items-baseline"
        style={{ gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <span style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 21, fontWeight: 500, letterSpacing: '0.1em', color: '#f2ece0' }}>HNL</span>
        <span style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontWeight: 300, fontSize: 18, color: '#b89a5a', letterSpacing: '0.06em' }}>Digital Luxe</span>
      </button>

      {/* ── Desktop nav ── */}
      <ul className="hidden lg:flex items-center list-none" style={{ gap: 36 }}>

        {/* Scroll links with smart active dot */}
        {navItems.map(({ label, sectionId }) => {
          const isActive = activeId === sectionId;
          return (
            <li key={label} style={{ position: 'relative' }}>
              <button
                onClick={() => scrollTo(sectionId)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontStyle: isActive ? 'italic' : 'normal',
                  fontSize: 16, fontWeight: isActive ? 400 : 300,
                  letterSpacing: '0.06em',
                  color: isActive ? '#d4b87a' : '#aea89f',
                  transition: 'color 0.25s ease, font-style 0.2s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = '#e8dcc8'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#aea89f'; }}
              >
                {label}
                <span style={{
                  position: 'absolute', bottom: -6, left: '50%',
                  transform: `translateX(-50%) scale(${isActive ? 1 : 0})`,
                  width: 4, height: 4, borderRadius: '50%', background: '#b89a5a', display: 'block',
                  transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                }} />
              </button>
            </li>
          );
        })}

        {/* Packages */}
        <li>
          <button
            onClick={() => onOpenModal('packages')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
              fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 16, fontWeight: 300,
              letterSpacing: '0.06em', color: '#aea89f', transition: 'color 0.25s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#e8dcc8')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#aea89f')}
          >
            Packages
          </button>
        </li>

        {/* HNL Insights — opens subdomain in new tab */}
        <li>
          <a
            href="https://insights.hnldigitalluxe.com"
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 16, fontWeight: 300, letterSpacing: '0.06em',
              color: '#aea89f', textDecoration: 'none',
              transition: 'color 0.25s ease',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#e8dcc8')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#aea89f')}
          >
            Insights
            {/* tiny external arrow */}
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.45, flexShrink: 0 }}>
              <path d="M2 8L8 2M8 2H4M8 2V6" stroke="#b89a5a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </li>

        {/* Work With Us */}
        <li>
          <button
            onClick={() => onOpenModal('workWithUs')}
            style={{
              fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: '#c8ab6a', padding: '9px 18px',
              border: '1px solid rgba(184,154,90,0.35)',
              background: 'transparent', cursor: 'pointer', transition: 'all 0.22s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#0d0c0b'; e.currentTarget.style.background = '#b89a5a'; e.currentTarget.style.borderColor = '#b89a5a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#c8ab6a'; e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(184,154,90,0.35)'; }}
          >
            Work With Us
          </button>
        </li>

        {/* Start With Clarity */}
        <li>
          <button
            onClick={() => scrollTo('clarity')}
            style={{
              fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              background: '#b89a5a', color: '#0d0c0b', border: 'none',
              padding: '10px 22px', cursor: 'pointer', transition: 'background 0.2s, transform 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#d4b87a'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#b89a5a'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Start With Clarity
          </button>
        </li>
      </ul>

      {/* ── Hamburger ── */}
      <button
        className="flex lg:hidden flex-col bg-transparent border-none cursor-pointer p-1"
        style={{ gap: 5 }}
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
      >
        {[
          { transform: mobileOpen ? 'translateY(6px) rotate(45deg)' : 'none' },
          { opacity: mobileOpen ? 0 : 1 },
          { transform: mobileOpen ? 'translateY(-6px) rotate(-45deg)' : 'none' },
        ].map((s, i) => (
          <span key={i} style={{ display: 'block', width: 22, height: 1, background: '#f2ece0', transition: 'all 0.25s ease', ...s }} />
        ))}
      </button>

      {/* ── Mobile menu ── */}
      <div
        className="fixed left-0 right-0 lg:hidden z-50"
        style={{
          top: 68, background: 'rgba(8,7,6,0.98)',
          borderBottom: '1px solid rgba(184,154,90,0.14)',
          padding: mobileOpen ? '28px 28px 32px' : '0 28px',
          maxHeight: mobileOpen ? 600 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.38s cubic-bezier(0.22,1,0.36,1), padding 0.38s ease',
        }}
      >
        <ul className="list-none" style={{ margin: 0, padding: 0 }}>
          {/* Scroll links */}
          {navItems.map((n) => (
            <li key={n.label} style={{ borderBottom: '1px solid rgba(184,154,90,0.08)' }}>
              <button
                onClick={() => scrollTo(n.sectionId)}
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: 300, letterSpacing: '0.06em', color: '#aea89f', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                {n.label}
                <span style={{ color: '#b89a5a', fontSize: 16, opacity: 0.5 }}>→</span>
              </button>
            </li>
          ))}

          {/* Packages */}
          <li style={{ borderBottom: '1px solid rgba(184,154,90,0.08)' }}>
            <button
              onClick={() => { onOpenModal('packages'); setMobileOpen(false); }}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: 300, letterSpacing: '0.06em', color: '#aea89f', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              Packages
              <span style={{ color: '#b89a5a', fontSize: 16, opacity: 0.5 }}>→</span>
            </button>
          </li>

          {/* HNL Insights — external link */}
          <li style={{ borderBottom: '1px solid rgba(184,154,90,0.08)' }}>
            <a
              href="https://insights.hnldigitalluxe.com"
              target="_blank"
              rel="noreferrer"
              onClick={() => setMobileOpen(false)}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: 300, letterSpacing: '0.06em', color: '#aea89f', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textDecoration: 'none' }}
            >
              Insights
              <svg width="14" height="14" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.4 }}>
                <path d="M2 8L8 2M8 2H4M8 2V6" stroke="#b89a5a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </li>

          {/* Work With Us */}
          <li style={{ borderBottom: '1px solid rgba(184,154,90,0.08)' }}>
            <button
              onClick={() => { onOpenModal('workWithUs'); setMobileOpen(false); }}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 0', fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: 300, letterSpacing: '0.06em', color: '#aea89f', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              Work With Us
              <span style={{ color: '#b89a5a', fontSize: 16, opacity: 0.5 }}>→</span>
            </button>
          </li>

          {/* Start With Clarity CTA */}
          <li style={{ paddingTop: 24 }}>
            <button
              onClick={() => scrollTo('clarity')}
              style={{ width: '100%', fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: '#b89a5a', color: '#0d0c0b', border: 'none', padding: '14px 20px', cursor: 'pointer' }}
            >
              Start With Clarity
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Nav;